/**
 * The SDP edits the voice client makes, ported from the spike's
 * scripts/lib/voice-spike/sdp.mjs (spikes do not ship, so this is a copy; keep
 * the two saying the same thing).
 *
 * Why the munge exists at all: a WebRTC sender uses Opus DTX only when the
 * REMOTE description's Opus fmtp carries usedtx=1. When this client publishes
 * to Cloudflare, the remote description is Cloudflare's answer, which the
 * client does not write - so it adds the parameter before setRemoteDescription.
 * DTX is what makes a room full of open-but-quiet mics cost almost nothing
 * (docs/VOICE_CHAT.md, "Cost guards").
 */

/** Payload types whose rtpmap is opus/48000/2, in order of appearance. */
export function opusPayloadTypes(sdp: string): string[] {
  const pts: string[] = [];
  for (const line of sdp.split(/\r?\n/)) {
    const m = /^a=rtpmap:(\d+) opus\/48000(?:\/2)?\s*$/i.exec(line);
    if (m) pts.push(m[1]!);
  }
  return pts;
}

/**
 * Set an Opus fmtp parameter on every Opus payload type: replaced if present,
 * appended if not, and an fmtp line created if the payload type has none.
 *
 * Line endings are preserved. SDP is CRLF, and a munge that rewrote it as LF
 * is rejected by some browsers' setRemoteDescription.
 */
export function setOpusParam(sdp: string, key: string, value: string): string {
  const eol = sdp.includes("\r\n") ? "\r\n" : "\n";
  const pts = new Set(opusPayloadTypes(sdp));
  if (pts.size === 0) return sdp;
  const lowerKey = `${key.toLowerCase()}=`;
  const seen = new Set<string>();
  const out: string[] = [];
  for (const line of sdp.split(/\r?\n/)) {
    const fm = /^a=fmtp:(\d+) (.*)$/.exec(line);
    if (fm && pts.has(fm[1]!)) {
      seen.add(fm[1]!);
      const params = fm[2]!
        .split(";")
        .map((p) => p.trim())
        .filter((p) => p && !p.toLowerCase().startsWith(lowerKey));
      params.push(`${key}=${value}`);
      out.push(`a=fmtp:${fm[1]} ${params.join(";")}`);
      continue;
    }
    out.push(line);
  }
  // Payload types with an rtpmap but no fmtp: add one right after the rtpmap.
  const result: string[] = [];
  for (const line of out) {
    result.push(line);
    const m = /^a=rtpmap:(\d+) opus\//i.exec(line);
    if (m && pts.has(m[1]!) && !seen.has(m[1]!)) {
      result.push(`a=fmtp:${m[1]} ${key}=${value}`);
      seen.add(m[1]!);
    }
  }
  return result.join(eol);
}

/** usedtx=1 on every Opus payload type. */
export function withDtx(sdp: string): string {
  return setOpusParam(sdp, "usedtx", "1");
}

/**
 * Everything the publish answer gets before it is applied: DTX, mono, and a
 * 32 kb/s ceiling. A phone mic is mono whatever the codec line says, and
 * stereo Opus at the browser's default rate is bandwidth spent on nothing a
 * table conversation needs.
 */
export function withVoiceOpus(sdp: string): string {
  return setOpusParam(setOpusParam(withDtx(sdp), "stereo", "0"), "maxaveragebitrate", "32000");
}

/** Reads an Opus fmtp parameter off the first Opus payload type, or null. */
export function opusParam(sdp: string, key: string): string | null {
  const [pt] = opusPayloadTypes(sdp);
  if (!pt) return null;
  const prefix = `a=fmtp:${pt} `;
  for (const line of sdp.split(/\r?\n/)) {
    if (!line.startsWith(prefix)) continue;
    for (const p of line.slice(prefix.length).split(";")) {
      const [k, v] = p.trim().split("=");
      if (k && k.toLowerCase() === key.toLowerCase()) return v ?? "";
    }
  }
  return null;
}
